import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { TCotacaoFornecedorInputs } from '.';

interface DialogConfirmarEnvioFornecedorProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export default function DialogConfirmarEnvioFornecedor({
    open,
    onClose,
    onConfirm,
}: DialogConfirmarEnvioFornecedorProps) {
    const { watch } = useFormContext<TCotacaoFornecedorInputs>();
    
    const formData = watch();
    
    const calcularTotalCotacao = () => {
        const totalProdutos = formData.produtos.reduce((acc, produto) => {
            const valor = parseFloat(produto.valor_total.replace(/\./g, '').replace(',', '.')) || 0;
            return acc + valor;
        }, 0);
        
        const valorFrete = parseFloat(formData.valor_frete.replace(/\./g, '').replace(',', '.')) || 0;
        
        return totalProdutos + valorFrete;
    };

    const formatarMoeda = (valor: string | number) => {
        if (typeof valor === 'string') {
            const numero = parseFloat(valor.replace(/\./g, '').replace(',', '.'));
            return `R$ ${numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        return `R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Typography variant="h5" fontWeight="bold">
                    Será enviado a cotação com os seguintes informações
                </Typography>
            </DialogTitle>
            
            <DialogContent dividers>
                <Box sx={{ py: 2 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Informações do Vendedor
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Vendedor
                                </Typography>
                                <Typography variant="body1">
                                    {formData.nome_vendedor || '-'}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Telefone
                                </Typography>
                                <Typography variant="body1">
                                    {formData.telefone_vendedor || '-'}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Vencimento
                                </Typography>
                                <Typography variant="body1">
                                    {formData.vencimento_cotacao ? formData.vencimento_cotacao.format('DD/MM/YYYY') : '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Configurações da Cotação
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Tipo de Frete
                                </Typography>
                                <Typography variant="body1">
                                    {formData.tipo_frete}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Valor do Frete
                                </Typography>
                                <Typography variant="body1">
                                    {formatarMoeda(formData.valor_frete)}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Prazo de Entrega
                                </Typography>
                                <Typography variant="body1">
                                    {formData.prazo ? `${formData.prazo} dias` : '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Produtos
                        </Typography>
                        <Paper variant="outlined" sx={{ mt: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell><b>Produto</b></TableCell>
                                        <TableCell align="center"><b>Quantidade</b></TableCell>
                                        <TableCell align="right"><b>Valor Unit.</b></TableCell>
                                        <TableCell align="right"><b>Valor Total</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {formData.produtos.map((produto, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {produto.nome}
                                                </Typography>
                                                {produto.codigo_produto_fornecedor && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Cód: {produto.codigo_produto_fornecedor}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {produto.quantidade} {produto.unidade_medida}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatarMoeda(produto.valor_unitario)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="medium">
                                                    {formatarMoeda(produto.valor_total)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell colSpan={3} align="right">
                                            <Typography fontWeight="bold">
                                                Subtotal Produtos:
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="bold">
                                                {formatarMoeda(formData.produtos.reduce((acc, p) => {
                                                    const valor = parseFloat(p.valor_total.replace(/\./g, '').replace(',', '.')) || 0;
                                                    return acc + valor;
                                                }, 0))}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={3} align="right">
                                            <Typography fontWeight="bold">
                                                Frete ({formData.tipo_frete}):
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="bold">
                                                {formatarMoeda(formData.valor_frete)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={3} align="right">
                                            <Typography fontWeight="bold" variant="h6">
                                                TOTAL:
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="bold" variant="h6">
                                                {formatarMoeda(calcularTotalCotacao())}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>

                    {formData.observacoes && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    Observações
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                        {formData.observacoes}
                                    </Typography>
                                </Paper>
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button 
                    onClick={onClose}
                    variant="outlined"
                    size="large"
                >
                    Cancelar
                </Button>
                <Button 
                    onClick={onConfirm}
                    variant="contained"
                    size="large"
                    color="success"
                >
                    Enviar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
